import algoliasearch from 'algoliasearch/lite';

const DEFAULT_ALGOLIA_BRANCH = 'master';
const DEFAULT_HITS_PER_PAGE = 8;
const algoliaClients = new Map();

function truncate(text, maxLength = 150) {
  if (!text) {
    return '';
  }

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength - 1)}…`;
}

const searchAliases = {
  Page: 'pages site route',
  Job: 'jobs careers roles hiring vacancy vacancies open roles',
  Resource: 'resources article articles insight insights',
  'Case study': 'case study case studies proof customer story',
  FAQ: 'faq faqs questions answers',
};

function slugify(value) {
  return (value || 'job')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function buildSearchIndex(siteContent) {
  const pageItems = Object.keys(siteContent.pages)
    .filter(
      (pageKey) =>
        !['privacyPolicy'].includes(pageKey) &&
        !['/resources', '/technology'].includes(siteContent.pages[pageKey]?.path)
    )
    .map((pageKey) => {
      const page = siteContent.pages[pageKey];
      const routeKeywords = page.path.replace(/\//g, ' ').replace(/-/g, ' ').trim();
      return {
        title: page.hero.title,
        description: page.hero.body,
        href: page.path,
        type: 'Page',
        keywords: routeKeywords,
      };
    });

  const resourceItems = siteContent.collections.resourceArticles.map((article) => ({
    title: article.title,
    description: article.summary,
    href: `/resources/${article.slug}`,
    type: 'Resource',
    keywords: article.keywords?.join(' ') || '',
  }));

  const jobItems = (siteContent.collections.jobs || []).map((job) => ({
    title: job.title,
    description: job.summary || job.description,
    href: `/jobs/${job.slug || slugify(job.title)}`,
    type: 'Job',
    keywords: [
      job.team,
      job.department,
      job.location,
      job.schedule,
      job.employmentType,
      job.body,
      job.callout,
      ...(job.expectations || []),
      ...(job.keywords || []),
    ]
      .filter(Boolean)
      .join(' '),
  }));

  const caseStudyItems = siteContent.collections.caseStudies.map((study) => ({
    title: study.title,
    description: study.description || study.summary,
    href: `/case-studies/${study.slug}`,
    type: 'Case study',
    keywords: [study.sector, ...(study.keywords || [])].filter(Boolean).join(' '),
  }));

  const faqItems = siteContent.collections.faqItems.map((item) => ({
    title: item.question,
    description: item.answer,
    href: `/faq#${item.slug}`,
    type: 'FAQ',
  }));

  return [...pageItems, ...jobItems, ...resourceItems, ...caseStudyItems, ...faqItems].map((item) => ({
    ...item,
    searchText: `${item.title} ${item.description} ${item.type} ${item.keywords || ''} ${searchAliases[item.type] || ''}`.toLowerCase(),
    description: truncate(item.description),
  }));
}

export function searchSite(index, query) {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return [];
  }

  return index
    .filter((item) => item.searchText.includes(normalizedQuery))
    .slice(0, 8);
}

export function getAlgoliaSearchConfig(searchSettings = {}, siteSettings = {}) {
  const appId = searchSettings.algoliaAppId || '';
  const apiKey = searchSettings.algoliaApiSearchKey || '';
  const siteId = searchSettings.algoliaSiteId || '';
  const branch = searchSettings.algoliaGitBranch || DEFAULT_ALGOLIA_BRANCH;

  if (!appId || !apiKey || !siteId) {
    return null;
  }

  return {
    appId,
    apiKey,
    siteId,
    branch,
    siteUrl: siteSettings.siteUrl || '',
    hitsPerPage: DEFAULT_HITS_PER_PAGE,
  };
}

export function buildAlgoliaIndexName(siteId, branch = DEFAULT_ALGOLIA_BRANCH) {
  return `netlify_${siteId}_${branch}_all`;
}

function getAlgoliaClient(appId, apiKey) {
  const cacheKey = `${appId}:${apiKey}`;

  if (!algoliaClients.has(cacheKey)) {
    algoliaClients.set(cacheKey, algoliasearch(appId, apiKey));
  }

  return algoliaClients.get(cacheKey);
}

function isSameOrigin(candidateUrl, siteUrl) {
  if (!candidateUrl || !siteUrl) {
    return false;
  }

  try {
    const candidate = new URL(candidateUrl);
    const site = new URL(siteUrl);
    const normalizeHost = (hostname) => hostname.replace(/^www\./, '');

    return candidate.protocol === site.protocol && normalizeHost(candidate.hostname) === normalizeHost(site.hostname);
  } catch (error) {
    return false;
  }
}

function normalizeAlgoliaHref(rawHref, siteUrl = '') {
  if (!rawHref) {
    return '/';
  }

  if (rawHref.startsWith('/')) {
    return rawHref;
  }

  try {
    const parsedUrl = new URL(rawHref);
    const runtimeOrigin = typeof window === 'undefined' ? '' : window.location.origin;
    const isInternal = isSameOrigin(parsedUrl.href, siteUrl) || isSameOrigin(parsedUrl.href, runtimeOrigin);

    if (isInternal) {
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    return rawHref;
  } catch (error) {
    return rawHref;
  }
}

function inferResultTypeFromHref(href) {
  if (href.startsWith('/jobs/')) {
    return 'Job';
  }

  if (href.startsWith('/resources/')) {
    return 'Resource';
  }

  if (href.startsWith('/case-studies/')) {
    return 'Case study';
  }

  if (href.startsWith('/faq')) {
    return 'FAQ';
  }

  return 'Page';
}

function buildTitleFromHref(href) {
  const path = href.split('#')[0].split('?')[0];
  const segments = path.split('/').filter(Boolean);
  const fallback = segments[segments.length - 1] || 'Search result';

  return fallback
    .split(/[-_]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export function mapAlgoliaHitToSearchResult(hit, siteUrl = '') {
  const href = normalizeAlgoliaHref(hit.url || hit.permalink || '/', siteUrl);
  const title =
    hit.title ||
    hit.hierarchy?.lvl0 ||
    hit.hierarchy?.lvl1 ||
    hit.description ||
    buildTitleFromHref(href);
  const description = truncate(
    hit.description ||
      hit.content ||
      hit.hierarchy?.lvl2 ||
      hit.hierarchy?.lvl1 ||
      ''
  );

  return {
    objectID: hit.objectID || href,
    href,
    title: truncate(title, 120),
    description,
    type: inferResultTypeFromHref(href),
  };
}

export async function searchAlgolia(config, query, options = {}) {
  const normalizedQuery = query.trim();

  if (!normalizedQuery) {
    return [];
  }

  const client = options.client || getAlgoliaClient(config.appId, config.apiKey);
  const index = client.initIndex(buildAlgoliaIndexName(config.siteId, config.branch));
  const response = await index.search(normalizedQuery, {
    hitsPerPage: config.hitsPerPage || DEFAULT_HITS_PER_PAGE,
    attributesToRetrieve: ['title', 'description', 'content', 'url', 'hierarchy'],
  });

  return (response.hits || []).map((hit) => mapAlgoliaHitToSearchResult(hit, config.siteUrl));
}
